import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import type { ApiClient } from "./api/client";
import { HttpApi } from "./api/http";
import { ServicesProvider, createServices, type Services } from "./app/services";
import { createAppRouter } from "./app/router";
import "./motion/preference";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";
import "./styles/pages.css";
import "./styles/motion.css";
import "./styles/brand-guide.css";

/**
 * つなぎ先：
 * - VITE_API_MODE=mock：開発用モック（`npm run dev` の既定）。画面に「開発用モック」と出る
 * - VITE_API_MODE=http：本物の API（`npm run build` の既定）。契約に無い操作は呼ばずに「未接続」と出す
 */

/** 文字の部品（Zen Maru Gothic）は、最初の表示を待たせないよう、あとから読む（参照元の配信ページと同じ考え方） */
function loadFonts() {
  void import("@fontsource/zen-maru-gothic/500.css");
  void import("@fontsource/zen-maru-gothic/700.css");
  // りばたんのひとこと用。使う文字の分だけ、必要になってから取りに行く
  void import("@fontsource/klee-one/600.css");
}

async function start() {
  const holder: { services?: Services } = {};
  let api: ApiClient;
  // 条件は式のまま書く（本番のビルドで定数になり、モックのコードが成果物に入らない）
  if (import.meta.env.VITE_API_MODE === "mock" || (import.meta.env.DEV && import.meta.env.VITE_API_MODE !== "http")) {
    const { MockApi, SCENARIOS } = await import("./mocks/mockApi");
    // 開発用：?mock=bot_offline のように、最初の状況を URL で選べる
    const wanted = new URLSearchParams(location.search).get("mock");
    const scenario = SCENARIOS.find((s) => s.id === wanted)?.id;
    api = new MockApi(scenario ? { scenario } : {});
  } else {
    // 401 が返ったら、ログインの画面に戻す
    api = new HttpApi({ onUnauthorized: () => holder.services?.session.setSignedOut() });
  }
  const services = createServices(api);
  holder.services = services;
  void services.session.load();
  const router = createAppRouter();
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ServicesProvider services={services}>
        <RouterProvider router={router} />
      </ServicesProvider>
    </StrictMode>,
  );
  if ("requestIdleCallback" in window) window.requestIdleCallback(loadFonts);
  else setTimeout(loadFonts, 300);
}

void start();
